/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from "react"
import { PublicBooking, Service, ServiceBooking, ServiceCustomForm } from "../types/service.type"
import { httpGet, httpPost } from "../services/Http.service"
import { AxiosResponse } from "axios"
import { apiUrl } from "../configs/config.constants"
import moment from "moment"
import { useGetProfile } from "./useGetProfile"
import { useNavigate } from "react-router"
import { DataContext, ProjectContext } from "../App"
import { array, object, string } from 'yup'
import { useFormik } from "formik"
import { useTranslation } from "react-i18next"
import { Project } from "../types/project.type"
import { CustomerQr, extractQrCode, mapCustomerQrToForm, splitPhone } from "../utils/customerQr"

export type BookingForm = {
  serviceIds: string[]
  reservationDate: string
  reservationTime: string
  isExternal: string
  attendedAddress: string
  customerName: string
  customerPhonePrefix: string
  customerPhoneNo: string
  customerEmail: string
  remark: string
  customForm: Record<string, unknown>
}

export function useBookingForm(
  selectedServices: Service[],
  setSelectedServices: (services: Service[]) => void,
) {
  const { project } = useContext(ProjectContext)
  const { services } = useContext(DataContext)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('booking')
  const { getAttendedBy, performedServices } = useGetProfile()
  const [recentServices, setRecentServices] = useState<Service[]>([])
  const [isReturningCustomer, setIsReturningCustomer] = useState(false)
  const [customForms, setCustomForms] = useState<ServiceCustomForm[]>([])
  const [availableTimes, setAvailableTimes] = useState<{ value: string, time: string }[]>([])
  const [listTimes, setListTimes] = useState<{ start: string, end: string }[]>([])
  const [customFormFields, setCustomFormFields] = useState<ServiceCustomForm[]>([])
  const [operationHours, setOperationHours] = useState<Project['operation']['onsite']>({
    interval: '',
    hours: []
  })
  const [allowExternalBooking, setAllowExternalBooking] = useState<boolean>(project?.allowExternalBooking || false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [qrStatus, setQrStatus] = useState<{ state: 'idle' | 'loading' | 'error' | 'success', message: string }>({
    state: 'idle',
    message: '',
  })

  const cachedCustomerData = JSON.parse(localStorage.getItem('customerData') || '{}')

  const validationSchema = useMemo(
    () =>
      object().shape({
        serviceIds: array().of(string()).min(1, t('validation.chooseService')),
        customerName: string().required(t('validation.enterFullName')),
        customerPhoneNo: string().required(t('validation.enterPhone')),
        customerPhonePrefix: string().required(t('validation.selectCountryCode')),
        // Empty string is treated as unset so the field stays optional.
        customerEmail: string()
          .transform((value) => (!value || !String(value).trim() ? undefined : String(value).trim()))
          .email(t('validation.enterValidEmail'))
          .optional(),
        isExternal: string()
          .oneOf(['true', 'false'], t('validation.chooseLocation'))
          .required(t('validation.chooseLocation')),
        attendedAddress: string().when('isExternal', {
          is: 'true',
          then: () => string().required(t('validation.enterAddress')),
          otherwise: () => string().optional().nullable(),
        }),
        reservationDate: string().required(t('validation.pickDate')),
        reservationTime: string().optional().nullable(),
        remark: string().optional().nullable(),
        customForm: object().optional().nullable(),
      }),
    [t, i18n.language]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      serviceIds: [],
      // No default — customer must pick when both location types are available.
      isExternal: '',
      customerName: cachedCustomerData.name || '',
      customerPhoneNo: cachedCustomerData.phoneNo || '',
      customerEmail: cachedCustomerData.email || '',
      reservationDate: '',
      reservationTime: '',
      attendedAddress: cachedCustomerData.address || '',
      customerPhonePrefix: cachedCustomerData.phonePrefix || '+62',
      remark: '',
      customForm: cachedCustomerData.customForm || {},
    },
    validationSchema,
    onSubmit: async (values: any) => {
      if (isSubmitting) return
      if (!validateCustomForm()) return

      setIsSubmitting(true)
      try {
        const customerBooking = await getCustomerBooking()
        if (customerBooking.length > 0) {
          setIsConfirmationModalOpen(true)
          // Keep isSubmitting=true while the modal is open; reset on cancel
          // (closeConfirmationModal) or after bookService completes.
          return
        }
        await bookService(values)
      } catch (error) {
        console.error(error)
        setIsSubmitting(false)
      }
    },
  })

  function closeConfirmationModal() {
    setIsConfirmationModalOpen(false)
    setIsSubmitting(false)
  }

  function getCustomFormErrors(): Record<string, string> {
    const errors: Record<string, string> = {}

    if (customFormFields.length === 0) return errors

    const customForm = (formik.values.customForm || {}) as Record<string, unknown>

    for (const form of customFormFields) {
      const value = customForm[form.key]

      if (form.required && (!value || value === '')) {
        errors[form.key] = t('validation.fieldRequired', { label: form.label || form.key })
      }
    }

    return errors
  }

  function validateCustomForm() {
    const errors = getCustomFormErrors()

    Object.entries(errors).forEach(([key, message]) => {
      formik.setFieldError(`customForm.${key}`, message)
      formik.setFieldTouched(`customForm.${key}`, true, false)
    })

    return Object.keys(errors).length === 0
  }

  const bookService = async (params: BookingForm) => {
    setIsSubmitting(true)

    try {
      const payload = {
        serviceId: params.serviceIds,
        reservationDateTime: moment(params.reservationDate + ' ' + params.reservationTime, 'YYYY-MM-DD HHmm'),
        isExternal: params.isExternal === 'true' ? true : false,
        attendedAddress: params.attendedAddress,
        customerName: params.customerName,
        customerPhoneNo: params.customerPhonePrefix + params.customerPhoneNo,
        customerEmail: params.customerEmail || undefined,
        remark: params.remark,
        customForm: params.customForm,
      }

      const response: AxiosResponse<PublicBooking> = await httpPost(apiUrl.booking.create, payload)

      const customerData = {
        name: params.customerName,
        phonePrefix: params.customerPhonePrefix,
        phoneNo: params.customerPhoneNo,
        email: params.customerEmail,
        address: params.attendedAddress,
        customForm: params.customForm,
        isExternal: params.isExternal === 'true' ? true : false,
      }
      localStorage.setItem('customerData', JSON.stringify(customerData))
      localStorage.setItem('bookingData', JSON.stringify(response.data))
      navigate('/confirmation', { state: { booking: response.data } })
    } catch (error: any) {
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    const customFormFields = customForms.filter((form) => {
      if (form.applyToAll) return true
      return form.serviceIds.some((serviceId) => selectedServices.some((service) => service.serviceId === serviceId))
    })
    setCustomFormFields(customFormFields.sort((a, b) => a.sequence - b.sequence))

    customFormFields.forEach((form) => {
      formik.setFieldValue(`customForm.${form.key}`, cachedCustomerData.customForm?.[form.key] || '')
    })
  }, [customForms, selectedServices])

  useEffect(() => {
    if (project) {
      getCustomForms()
      getAttendedBy()
    }
  }, [project])

  useEffect(() => {
    if (formik.values.reservationDate) {
      const day = moment(formik.values.reservationDate).day()
      populateTime(day)
    }
  }, [
    operationHours,
    formik.values.reservationDate,
    selectedServices,
    listTimes,
    performedServices,
  ])

  useEffect(() => {
    if (project?.allowExternalBooking && selectedServices.length > 0) {
      const allowExternalBooking = selectedServices
        .filter((service) => !!service.allowExternalBooking)
        .length > 0
      setAllowExternalBooking(allowExternalBooking)

      if (!allowExternalBooking) {
        formik.setFieldValue('isExternal', 'false')
      }
      return
    }

    setAllowExternalBooking(project?.allowExternalBooking || false)
  }, [project, selectedServices])

  // When the location picker is not shown, set the only available mode automatically.
  useEffect(() => {
    if (!project) return
    const both =
      !!project.allowExternalBooking && !!project.allowOffsiteBooking
    if (both) return

    if (project.allowExternalBooking && !project.allowOffsiteBooking) {
      if (formik.values.isExternal !== 'true') {
        formik.setFieldValue('isExternal', 'true')
      }
      return
    }

    if (formik.values.isExternal !== 'false') {
      formik.setFieldValue('isExternal', 'false')
    }
  }, [project])

  useEffect(() => {
    if (project && formik.values.reservationDate) {
      getListTimes()
    }
  }, [
    project,
    formik.values.reservationDate,
    selectedServices,
    formik.values.isExternal,
  ])

  useEffect(() => {
    if (project) {
      setOperationHours(formik.values.isExternal === 'true' ? project.operation?.offsite : project.operation?.onsite)
    }
  }, [project, formik.values.isExternal])

  /**
   * Fills the contact fields from a customer QR.
   *
   * The booking payload still identifies the customer by phone rather than by the
   * resolved id: this form is public, so accepting a customer id from it would let
   * anyone attach a booking to someone else's record. The resolved phone is the
   * stored one, so the backend matches the existing customer anyway.
   */
  async function applyCustomerQr(code: string) {
    if (!code) return

    setQrStatus({ state: 'loading', message: '' })
    try {
      const response: AxiosResponse<CustomerQr> = await httpPost(apiUrl.customerQr.resolve, { code })
      const fields = mapCustomerQrToForm(response.data)

      Object.entries(fields).forEach(([field, value]) => {
        if (value) formik.setFieldValue(field, value)
      })

      setQrStatus({ state: 'success', message: t('qr.filled', { name: response.data.name }) })
      const { prefix, number } = splitPhone(response.data.phoneNo)
      lookupRecentBookings(prefix + number)
    } catch (error) {
      console.error(error)
      setQrStatus({ state: 'error', message: t('qr.invalid') })
    }
  }

  // A QR opened straight from a phone camera lands here as ?c=<code>. Wait for the
  // project, since resolving needs the project-id header it puts in localStorage.
  useEffect(() => {
    if (!project) return

    const code = extractQrCode(window.location.search)
    if (code) applyCustomerQr(code)
  }, [project])

  async function lookupRecentBookings(fullPhone: string) {
    if (!fullPhone || !project?.projectId) return
    try {
      const response: AxiosResponse<ServiceBooking[]> = await httpGet(apiUrl.booking.customerRecent, {
        phoneNo: fullPhone,
        limit: 5,
      })
      const bookings = response?.data || []
      setIsReturningCustomer(bookings.length > 0)

      // Only surface services that still exist and are bookable today.
      const liveById = new Map(services.map((s) => [s.serviceId, s]))
      const seen = new Set<string>()
      const recent: Service[] = []
      bookings.forEach((booking) => {
        (booking.serviceJson || []).forEach((svc) => {
          const live = liveById.get(svc.serviceId)
          if (live && !seen.has(live.serviceId)) {
            seen.add(live.serviceId)
            recent.push(live)
          }
        })
      })
      setRecentServices(recent)
    } catch (error) {
      console.error(error)
    }
  }

  async function getCustomerBooking() {
    const response = await httpGet(apiUrl.booking.customerBooking, {
      phoneNo: formik.values.customerPhonePrefix + formik.values.customerPhoneNo,
      reservationDateTime: moment(formik.values.reservationDate).toDate(),
    })
    return response?.data
  }

  function getListTimes() {
    const params = {
      dateFrom: moment(formik.values.reservationDate).startOf('day'),
      dateTo: moment(formik.values.reservationDate).endOf('day'),
    }
    httpGet(apiUrl.booking.checkSlots, params)
      .then((response: AxiosResponse<{ start: string, end: string }[]>) => {
        setListTimes(response.data)
      })
  }

  function getCustomForms() {
    httpGet(apiUrl.customForm.list)
      .then((response: AxiosResponse<ServiceCustomForm[]>) => {
        const customForms = response.data
        setCustomForms(customForms)
      })
      .catch((error: any) => {
        console.error(error)
      })
  }

  function processTimeSlot(
    openTime: string,
    closeTime: string,
    totalServiceTime: number,
    day: number,
    availableTimes: { value: string, time: string }[]
  ): void {
    let i = Number(moment(openTime, 'HH:mm').format('HHmm')) || 800
    let endWorkingTime = Number(moment(closeTime, 'HH:mm').format('HHmm')) || 1800

    while (i <= endWorkingTime) {
      const currentTimeString = i.toString().padStart(4, '0')

      const startServiceTime = i
      const endServiceTime = Number(moment(currentTimeString, 'HHmm')
        .add(totalServiceTime, 'minutes')
        .format('HHmm'))

      if (endServiceTime > endWorkingTime) {
        break
      }

      let isAvailable = true
      let conflictReservationsCount = 0
      const attendedByCount = getPerformedServicesCount(selectedServices, day, startServiceTime)
      if (listTimes.length > 0) {
        for (const unavailableTime of listTimes) {
          const unavailableTimeStartLocal = Number(moment.utc(unavailableTime.start, 'HHmm').local().format('HHmm'))
          const unavailableTimeEndLocal = Number(moment.utc(unavailableTime.end, 'HHmm').local().format('HHmm'))
          if (
            (startServiceTime >= unavailableTimeStartLocal && startServiceTime < unavailableTimeEndLocal) ||
            (endServiceTime > unavailableTimeStartLocal && endServiceTime <= unavailableTimeEndLocal)
          ) {
            conflictReservationsCount++
            if (conflictReservationsCount >= attendedByCount) {
              isAvailable = false
              break
            }
          }
        }
      } else {
        isAvailable = attendedByCount > 0
      }

      if (isAvailable) {
        availableTimes.push({
          value: currentTimeString,
          time: startServiceTime.toString().length > 3 ?
            `${startServiceTime.toString().substring(0, 2)}:${startServiceTime.toString().substring(2, 4)}` :
            `0${startServiceTime.toString().substring(0, 1)}:${startServiceTime.toString().substring(1, 3)}`,
        });
      }

      i = Number(moment(currentTimeString, 'HHmm')
        .add(Number(operationHours?.interval) || 30, 'minutes')
        .format('HHmm'))
    }
  }

  function generateTimeSlots(
    day: number,
  ): { value: string, time: string }[] {
    if (Number(operationHours?.interval) >= (60 * 24)) {
      return []
    }

    const availableTimes: { value: string, time: string }[] = []
    const operationDay = operationHours?.hours?.[day]
    const totalServiceTime = selectedServices.reduce((acc: number, curr: any) => acc + curr.unitValue, 0)

    if (operationDay?.time && operationDay.time.length > 0) {
      operationDay.time.forEach((timeSlot: any) => {
        processTimeSlot(
          timeSlot.openTime,
          timeSlot.closeTime,
          totalServiceTime,
          day,
          availableTimes
        )
      })
    } else {
      processTimeSlot(
        '0800',
        '1800',
        totalServiceTime,
        day,
        availableTimes
      )
    }

    return availableTimes
  }

  function populateTime(day: number) {
    const availableTimes = generateTimeSlots(day)
    const currentSelectedTime = formik.values.reservationTime

    const isCurrentTimeAvailable = availableTimes.some(time => time.value === currentSelectedTime)
    if (!isCurrentTimeAvailable && availableTimes.length > 0) {
      formik.setFieldValue('reservationTime', availableTimes[0]?.value)
    }

    setAvailableTimes(availableTimes)
  }

  function getPerformedServicesCount(
    selectedServices: Service[],
    day: number,
    startServiceTime: number,
  ) {
    const locationType = formik.values.isExternal === 'true' ? 'offsite' : 'onsite'
    const result = performedServices
      .filter((attendedBy) => {
        const servicesWithAttendedBy = selectedServices.filter((service) => service.allowAttendedBy?.length && service.allowAttendedBy.length > 0)
        if (servicesWithAttendedBy.length === 0) return true

        return selectedServices.every((service) => service.allowAttendedBy?.includes(attendedBy.userId))
      })
      .filter((attendedBy) => {
        const attendedByHours = attendedBy.operation?.[locationType]?.hours?.[day]

        if (!attendedByHours) return true
        if (!attendedByHours.isOpen) return false

        if (attendedByHours?.time && attendedByHours.time.length > 0) {
          return attendedByHours.time.some((timeSlot: any) => {
            const attendedByStart = Number(moment(timeSlot.openTime, 'HH:mm').format('HHmm'))
            const attendedByEnd = Number(moment(timeSlot.closeTime, 'HH:mm').format('HHmm'))
            return (startServiceTime >= attendedByStart && startServiceTime < attendedByEnd)
          })
        }
        return true
      })

    return result.length
  }

  return {
    selectedServices,
    setSelectedServices,
    customForms,
    availableTimes,
    formik,
    customFormFields,
    getCustomFormErrors,
    operationHours,
    populateTime,
    allowExternalBooking,
    bookService,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    closeConfirmationModal,
    isSubmitting,
    recentServices,
    isReturningCustomer,
    lookupRecentBookings,
    applyCustomerQr,
    qrStatus,
    setQrStatus,
  }
}